import moment from "moment";
import buildUrl from "../utils/buildUrl";
import { baseUrl } from "./baseUrl";
import {
  IServiceTypeSearch,
  IOfferListSearch,
  IFindAlternativeOfferParams,
} from "../../store/slices/offering/types";
import { toHttpFormat } from "../utils";
import { IOfferPriceCloseParams } from "../types/OfferPrice";

export const fetchOfferListRoute = (searchParams: Partial<IOfferListSearch<string>>) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "offering", "search"].join("/"),
    searchParams,
  });

export const fetchOfferItemByIdRoute = (id: number, searchParams) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "offering-items", `${id}`].join("/"),
    searchParams,
  });

export const fetchServiceTypeListRoute = (searchParams: Partial<IServiceTypeSearch>) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-types", "search"].join("/"),
    searchParams,
  });

export const createOfferRoute = () =>
  buildUrl({
    url: [baseUrl, "offerings-api", "offering"].join("/"),
  });

export const updateOfferRoute = (props: { id: number; fromDate?: string; force?: boolean }) => {
  const { id, fromDate, force = false } = props;
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering", id].join("/"),
    searchParams: {
      fromDate,
      force,
    },
  });
};

export const closeOfferVersionRoute = (props: { id: number; fromDate?: string }) => {
  const { id, fromDate } = props;
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering", id].join("/"),
    searchParams: {
      fromDate,
    },
  });
};

export const fetchOfferByIdRoute = (params: { id: number; onDate: string }) => {
  const { id, onDate } = params;
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering", id].join("/"),
    searchParams: {
      onDate,
    },
  });
};

export const fetchOfferVersionsByIdRoute = (
  id: number,
  searchParams?: {
    allObjectsVersions?: boolean;
    withChars?: boolean;
  }
) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "offering", id, "versions"].join("/"),
    searchParams,
  });

export const fetchServiceProfileVersionsRoute = (id: number) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-profiles", id, "versions"].join("/"),
  });

export const fetchOfferServiceProfileByIdRoute = (id: number, onDate?: string) => {
  if (onDate) {
    return buildUrl({
      url: [baseUrl, "offerings-api", "service-profiles", id].join("/"),
      searchParams: {
        onDate,
      },
    });
  }
  return buildUrl({
    url: [baseUrl, "offerings-api", "service-profiles", id].join("/"),
  });
};

export const updateOfferServiceProfileRoute = (params: {
  id: number;
  fromDate: string;
  force?: boolean;
}) => {
  const { id, fromDate, force = false } = params;
  return buildUrl({
    url: [baseUrl, "offerings-api", "service-profiles", id].join("/"),
    searchParams: {
      fromDate,
      force,
    },
  });
};

export const fetchOfferHierarchyRoute = (params: { id: number; onDate?: string }) => {
  const { id, onDate } = params;
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering-hierarchy", id].join("/"),
    searchParams: {
      onDate: onDate || toHttpFormat(moment()),
    },
  });
};

export const createOfferHierarchyRoute = (id: number) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering-hierarchy", id].join("/"),
    searchParams: { checkStatuses: true },
  });
};

export const fetchCurrentOfferHierarchyVersionsRoute = (id: number) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering-hierarchy", id, "versions"].join("/"),
  });
};

export const fetchServiceRateVersionsRoute = (id: number) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", id, "versions"].join("/"),
  });
};

export const fetchServiceRatesVersionsRoute = (ids: number[]) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", "versions"].join("/"),
    searchParams: { ids },
  });
};

export const closeOfferHierarchyRoute = (searchParams: {
  offeringLinkIds: number[];
  fromDate: string;
}) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering-hierarchy", "close"].join("/"),
    searchParams,
  });
};

export const deleteOfferHierarchyRoute = (offeringLinkIds: number[]) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering-hierarchy", "delete"].join("/"),
    searchParams: { offeringLinkIds },
  });
};

export const validateOfferAvailibilityRoute = (searchParams: {
  agreementId: number;
  offeringId: number;
  offeringStatusIds?: number[];
  onDate?: string;
  dependencyCheck?: boolean;
}) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering", "available", "check"].join("/"),
    searchParams,
  });
};

export const findAlternativeProlongOfferRoute = (searchParams: IFindAlternativeOfferParams) => {
  return buildUrl({
    url: [baseUrl, "offerings-api", "offering", "alternativeOffering"].join("/"),
    searchParams,
  });
};

export const createOfferPriceRoute = () =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-rates"].join("/"),
  });

export const editOfferPriceRoute = (rateId: number, fromDate: string, force: boolean) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", rateId].join("/"),
    searchParams: {
      fromDate,
      force,
    },
  });

export const closeOfferPriceRoute = (params: IOfferPriceCloseParams) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", params.rateId].join("/"),
    searchParams: {
      fromDate: params.fromDate,
    },
  });

export const fetchOfferPriceRoute = (params: { rateId: string | number; onDate?: string }) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", params.rateId].join("/"),
    searchParams: {
      onDate: params.onDate,
    },
  });

export const deleteOfferPriceRoute = (rateId: number) =>
  buildUrl({
    url: [baseUrl, "offerings-api", "service-rates", rateId, "delete"].join("/"),
  });
